import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { locationService, areaService, extraService, insuranceService, settingsService } from '../api/services';

function genId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

// ── Moved outside component to prevent remount on every keystroke ──
const Card = ({ children, style }) => (
  <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 16, boxShadow: 'var(--sh)', marginBottom: 14, ...style }}>
    {children}
  </div>
);
const SectionHead = ({ icon, title, sub, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
    <div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{icon} {title}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>{sub}</div>}
    </div>
    {action}
  </div>
);

function ConfirmDialog({ message, onConfirm, onCancel }) {
  if (!message) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14,
        boxShadow: '0 8px 40px rgba(0,0,0,0.25)', padding: '28px 32px',
        minWidth: 320, maxWidth: 420, textAlign: 'center',
      }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx)', marginBottom: 8 }}>Are you sure?</div>
        <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 24, lineHeight: 1.6 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-ghost" style={{ minWidth: 90 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" style={{ minWidth: 90 }} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

const INS_ICON_PRESETS = [
  { icon: '🛡️',             label: '1 Shield — Basic' },
  { icon: '🛡️🛡️',          label: '2 Shields — Standard' },
  { icon: '🛡️🛡️🛡️',       label: '3 Shields — Full Cover' },
  { icon: '⭐',              label: 'Star' },
  { icon: '⭐⭐',            label: '2 Stars' },
  { icon: '⭐⭐⭐',          label: '3 Stars' },
  { icon: '✅',              label: 'Checkmark' },
  { icon: '🔰',              label: 'Badge' },
  { icon: '💎',              label: 'Diamond' },
  { icon: '🏅',              label: 'Medal' },
  { icon: '🪙',              label: 'Coin' },
  { icon: '🔒',              label: 'Lock' },
];

const EXTRA_PRESETS = [
  { icon: '🗺️', name: 'GPS Navigation' },
  { icon: '🪑', name: 'Child Seat' },
  { icon: '👶', name: 'Baby / Infant Seat' },
  { icon: '🚗', name: 'Booster Seat' },
  { icon: '🔌', name: 'Phone Charger' },
  { icon: '🧑‍🤝‍🧑', name: 'Additional Driver' },
  { icon: '🧑', name: 'Young Driver (21-25)' },
  { icon: '👴', name: 'Senior Driver (71-75)' },
  { icon: '📦', name: 'Roof Box' },
  { icon: '⛓️', name: 'Snow Chains' },
  { icon: '⛽', name: 'Full Tank Option' },
  { icon: '✈️', name: 'Airport Meet & Greet' },
  { icon: '⏰', name: 'Out of Hours Pickup' },
  { icon: '🅿️', name: 'Parking Pass' },
  { icon: '🛞', name: 'Spare Tyre' },
  { icon: '📡', name: 'Wi-Fi Hotspot' },
  { icon: '🪣', name: 'Cleaning Kit' },
  { icon: '🛡️', name: 'Windscreen Protection' },
];

export default function BookingSetupPage() {
  const {
    companySettings, setCompanySettings,
    locations, setLocations,
    extras, setExtras,
    insurance, setInsurance,
    areas, setAreas,
    showToast, openModal,
  } = useApp();

  const [settings, setSettings] = useState(companySettings);

  // Edit state: { section: 'loc'|'extra'|'ins', id, draft }
  const [editing, setEditing] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);

  // Collapsible sections
  const [openSections, setOpenSections] = useState({ loc: false, extra: false, ins: false, areas: false, rules: false, reqs: false });
  function toggleSection(key) { setOpenSections(s => ({ ...s, [key]: !s[key] })); }

  // Custom confirm dialog
  const [confirmDlg, setConfirmDlg] = useState(null); // { message, onConfirm }
  function askConfirm(message, onConfirm) {
    setConfirmDlg({ message, onConfirm });
  }
  function closeConfirm() { setConfirmDlg(null); }

  function save() {
    setCompanySettings(settings);
    settingsService.update(settings).catch(() => {});
    showToast('✓ Settings saved');
  }

  const bookingUrl = `fleetrent.app/book/${settings.slug || 'yourslug'}`;

  // ── Generic edit helpers ──
  function startEdit(section, item) {
    const label = section === 'loc' ? 'location' : section === 'extra' ? 'extra' : section === 'area' ? 'area' : 'insurance plan';
    askConfirm(`You are about to edit this ${label}. Any unsaved changes will be lost.`, () => {
      setConfirmDlg(null);
      setEditing({ section, id: item.id, draft: { ...item } });
    });
  }
  function cancelEdit() { setEditing(null); }
  function patchDraft(field, val) {
    setEditing(e => ({ ...e, draft: { ...e.draft, [field]: val } }));
  }

  // ── Locations ──
  function saveLoc() {
    askConfirm('Please confirm that you want to save the changes to this location.', () => {
      setConfirmDlg(null);
      const draft = editing.draft;
      const backendId = draft._backendId || draft.id;
      setLocations(prev => prev.map(l => l.id === draft.id ? draft : l));
      setEditing(null);
      locationService.update(backendId, draft).catch(() => {});
      showToast('✓ Location saved');
    });
  }
  function setDefaultLoc(id) {
    setLocations(prev => prev.map(l => ({ ...l, isDefault: l.id === id })));
    showToast('✓ Default location updated');
  }
  function addLoc() {
    const newLoc = { id: genId(), name: 'New Location', address: '', fee: 0, isDefault: false };
    locationService.create(newLoc).then(created => {
      setLocations(prev => [...prev, created]);
      setEditing({ section: 'loc', id: created.id, draft: { ...created } });
    }).catch(() => {
      setLocations(prev => [...prev, newLoc]);
      setEditing({ section: 'loc', id: newLoc.id, draft: { ...newLoc } });
    });
  }
  function removeLoc(id) {
    if (locations.length <= 1) { showToast('Need at least 1 location'); return; }
    askConfirm('Please confirm that you want to delete this location. This cannot be undone.', () => {
      setConfirmDlg(null);
      if (editing?.id === id) setEditing(null);
      const loc = locations.find(l => l.id === id);
      const backendId = loc?._backendId || id;
      setLocations(prev => prev.filter(l => l.id !== id));
      locationService.delete(backendId).catch(() => {});
      showToast('Location removed');
    });
  }

  // ── Extras ──
  function saveExtra() {
    askConfirm('Please confirm that you want to save the changes to this extra.', () => {
      setConfirmDlg(null);
      const draft = editing.draft;
      const backendId = draft._backendId || draft.id;
      setExtras(prev => prev.map(e => e.id === draft.id ? draft : e));
      setEditing(null);
      extraService.update(backendId, draft).catch(() => {});
      showToast('✓ Extra saved');
    });
  }
  function addExtra() {
    const item = { id: genId(), name: 'New Extra', icon: '➕', price: 5, perDay: true, enabled: true };
    extraService.create(item).then(created => {
      setExtras(prev => [...prev, created]);
      setEditing({ section: 'extra', id: created.id, draft: { ...created } });
    }).catch(() => {
      setExtras(prev => [...prev, item]);
      setEditing({ section: 'extra', id: item.id, draft: { ...item } });
    });
  }
  function removeExtra(id) {
    askConfirm('Please confirm that you want to delete this extra. This cannot be undone.', () => {
      setConfirmDlg(null);
      if (editing?.id === id) setEditing(null);
      const extra = extras.find(e => e.id === id);
      const backendId = extra?._backendId || id;
      setExtras(prev => prev.filter(e => e.id !== id));
      extraService.delete(backendId).catch(() => {});
      showToast('Extra removed');
    });
  }

  // ── Insurance ──
  function saveIns() {
    askConfirm('Please confirm that you want to save the changes to this insurance plan.', () => {
      setConfirmDlg(null);
      const draft = editing.draft;
      const backendId = draft._backendId || draft.id;
      setInsurance(prev => prev.map(i => i.id === draft.id ? draft : i));
      setEditing(null);
      insuranceService.update(backendId, draft).catch(() => {});
      showToast('✓ Insurance plan saved');
    });
  }
  function addIns() {
    const item = { id: genId(), name: 'New Plan', icon: '🛡️', price: 0, perDay: true, desc: '', excess: '€0', deposit: 0, color: 'var(--bd)' };
    insuranceService.create(item).then(created => {
      setInsurance(prev => [...prev, created]);
      setEditing({ section: 'ins', id: created.id, draft: { ...created } });
    }).catch(() => {
      setInsurance(prev => [...prev, item]);
      setEditing({ section: 'ins', id: item.id, draft: { ...item } });
    });
  }
  function removeIns(id) {
    if (insurance.length <= 1) { showToast('Need at least 1 insurance type'); return; }
    askConfirm('Please confirm that you want to delete this insurance plan. This cannot be undone.', () => {
      setConfirmDlg(null);
      if (editing?.id === id) setEditing(null);
      const plan = insurance.find(i => i.id === id);
      const backendId = plan?._backendId || id;
      setInsurance(prev => prev.filter(i => i.id !== id));
      insuranceService.delete(backendId).catch(() => {});
      showToast('Insurance plan removed');
    });
  }

  // ── Areas ──
  function saveArea() {
    askConfirm('Please confirm that you want to save the changes to this area.', () => {
      setConfirmDlg(null);
      const draft = editing.draft;
      const backendId = draft._backendId || draft.id;
      setAreas(prev => prev.map(a => a.id === draft.id ? draft : a));
      setEditing(null);
      areaService.update(backendId, draft).catch(() => {});
      showToast('✓ Area saved');
    });
  }
  function addArea() {
    const item = { id: genId(), name: 'New Area', icon: '📍', color: '#1A3A5C' };
    areaService.create(item).then(created => {
      setAreas(prev => [...prev, created]);
      setEditing({ section: 'area', id: created.id, draft: { ...created } });
    }).catch(() => {
      setAreas(prev => [...prev, item]);
      setEditing({ section: 'area', id: item.id, draft: { ...item } });
    });
  }
  function removeArea(id) {
    askConfirm('Please confirm that you want to delete this area. This cannot be undone.', () => {
      setConfirmDlg(null);
      if (editing?.id === id) setEditing(null);
      const area = areas.find(a => a.id === id);
      const backendId = area?._backendId || id;
      setAreas(prev => prev.filter(a => a.id !== id));
      areaService.delete(backendId).catch(() => {});
      showToast('Area removed');
    });
  }

  const isEditing = (section, id) => editing?.section === section && editing?.id === id;

  return (
    <div>
      <ConfirmDialog
        message={confirmDlg?.message}
        onConfirm={confirmDlg?.onConfirm}
        onCancel={closeConfirm}
      />
      {/* Preview banner */}
      <div style={{
        background: `linear-gradient(135deg, ${settings.brandColor || '#1A3A5C'}, #0f2840)`,
        borderRadius: 'var(--rl)', padding: 16, marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🚗</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{settings.name || 'Your Company'}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>Vehicle & Equipment Rental</div>
          </div>
        </div>
        <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 6, padding: '6px 12px', fontSize: 11, color: 'rgba(255,255,255,.8)', fontFamily: "'DM Mono',monospace" }}>
          {bookingUrl}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <Card style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>⚙️ Booking Page Settings</div>
            {!editingSettings && (
              <button className="btn btn-ghost btn-sm" onClick={() => askConfirm('Are you sure you want to edit the booking page settings?', () => { setConfirmDlg(null); setEditingSettings(true); })}>Edit</button>
            )}
          </div>
          {editingSettings ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="fg"><label className="fl">Company name</label>
                <input type="text" className="fi" value={settings.name || ''} onChange={e => setSettings(s => ({ ...s, name: e.target.value }))} autoFocus />
              </div>
              <div className="fg"><label className="fl">URL slug</label>
                <input type="text" className="fi" value={settings.slug || ''} onChange={e => setSettings(s => ({ ...s, slug: e.target.value }))} />
              </div>
              <div className="fg"><label className="fl">Phone on booking page</label>
                <input type="text" className="fi" value={settings.phone || ''} onChange={e => setSettings(s => ({ ...s, phone: e.target.value }))} />
              </div>
              <div className="fg"><label className="fl">Booking confirmation</label>
                <select className="fsel2" value={settings.bookingConfirmation || 'manual'} onChange={e => setSettings(s => ({ ...s, bookingConfirmation: e.target.value }))}>
                  <option value="manual">Manual — you review each booking</option>
                  <option value="auto">Auto-confirm if available</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={() => askConfirm('Please confirm that you want to save the changes to the booking page settings.', () => { setConfirmDlg(null); save(); setEditingSettings(false); })}>Save</button>
                <button className="btn btn-ghost" onClick={() => { setSettings(companySettings); setEditingSettings(false); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--tx3)' }}>Company name</span>
                <span style={{ fontWeight: 600 }}>{settings.name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--tx3)' }}>URL slug</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{settings.slug || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--tx3)' }}>Phone</span>
                <span>{settings.phone || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--tx3)' }}>Confirmation</span>
                <span>{settings.bookingConfirmation === 'auto' ? 'Auto-confirm' : 'Manual review'}</span>
              </div>
            </div>
          )}
        </Card>

        <Card style={{ marginBottom: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Your Booking Link</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '7px 10px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: 'var(--tx2)', flex: 1 }}>{bookingUrl}</div>
            <button className="btn btn-primary btn-sm" onClick={() => showToast('Link copied!')}>Copy</button>
          </div>
          <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => openModal('booking-engine')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 13, height: 13 }}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20"/></svg>
            Preview booking page
          </button>
        </Card>
      </div>

      {/* ── Locations ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('loc')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>📍 Pickup & Return Locations
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>{locations.length} location{locations.length !== 1 ? 's' : ''}</span>
            </div>
            {!openSections.loc && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          {openSections.loc && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addLoc(); }}>+ Add Location</button>}
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.loc ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.loc && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {locations.map(loc => (
            <div key={loc.id} style={{
              border: `1.5px solid ${loc.isDefault ? 'var(--gr-b)' : 'var(--bd)'}`,
              borderRadius: 'var(--r)', padding: '10px 12px',
              background: loc.isDefault ? 'var(--gr-l)' : 'var(--sur2)',
            }}>
              {isEditing('loc', loc.id) ? (
                /* Edit mode */
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 80px 120px', gap: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Location name</div>
                      <input type="text" className="fi" value={editing.draft.name}
                        onChange={e => patchDraft('name', e.target.value)} placeholder="e.g. Paphos Office" autoFocus />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Address</div>
                      <input type="text" className="fi" value={editing.draft.address}
                        onChange={e => patchDraft('address', e.target.value)} placeholder="Full address" />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Delivery fee (€)</div>
                      <input type="number" className="fi" min={0} value={editing.draft.fee}
                        onChange={e => patchDraft('fee', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Area</div>
                      <select className="fsel2" value={editing.draft.areaId || ''}
                        onChange={e => patchDraft('areaId', e.target.value || null)}>
                        <option value="">— No area —</option>
                        {areas.map(a => <option key={a.id} value={a.id}>{a.icon} {a.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveLoc}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
                    <button onClick={() => removeLoc(loc.id)}
                      style={{ background: 'none', border: '1px solid var(--ac)', color: 'var(--ac)', cursor: 'pointer', fontSize: 13, borderRadius: 6, padding: '2px 8px', marginLeft: 'auto' }}>✕ Delete</button>
                  </div>
                </>
              ) : (
                /* Read mode */
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>📍</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{loc.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{loc.address}</div>
                    {loc.fee > 0 && <div style={{ fontSize: 10, color: 'var(--am)' }}>+€{loc.fee} delivery fee</div>}
                    {loc.areaId && (() => { const area = areas.find(a => a.id === loc.areaId); return area ? (
                      <span style={{ display: 'inline-block', fontSize: 9, padding: '1px 7px', borderRadius: 8, marginTop: 2, fontWeight: 600, background: area.color + '18', color: area.color, border: `1px solid ${area.color}33` }}>📍 {area.name}</span>
                    ) : null; })()}
                    {loc.isDefault && <div style={{ fontSize: 10, color: 'var(--gr)', fontWeight: 600 }}>✓ Default location</div>}
                  </div>
                  {!loc.isDefault && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setDefaultLoc(loc.id)}>Set default</button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit('loc', loc)}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>}
      </Card>

      {/* ── Extras ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('extra')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>➕ Extras & Add-ons
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>{extras.length} extra{extras.length !== 1 ? 's' : ''}</span>
            </div>
            {!openSections.extra && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          {openSections.extra && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addExtra(); }}>+ Add Extra</button>}
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.extra ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.extra && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {extras.map(ex => (
            <div key={ex.id} style={{
              border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px',
              background: 'var(--sur2)', opacity: ex.enabled ? 1 : 0.55,
            }}>
              {isEditing('extra', ex.id) ? (
                /* Edit mode */
                <>
                  {/* Presets */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick presets</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {EXTRA_PRESETS.map(p => (
                        <button key={p.name} onClick={() => { patchDraft('icon', p.icon); patchDraft('name', p.name); }}
                          title={p.name}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                            border: editing.draft.name === p.name ? '1.5px solid var(--ac)' : '1px solid var(--bd)',
                            background: editing.draft.name === p.name ? 'var(--ac-l)' : 'var(--sur)',
                            color: editing.draft.name === p.name ? 'var(--ac)' : 'var(--tx2)',
                            fontWeight: editing.draft.name === p.name ? 600 : 400,
                          }}>
                          <span>{p.icon}</span>
                          <span style={{ fontSize: 10 }}>{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 90px 90px 60px', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Icon</div>
                      <input type="text" value={editing.draft.icon} onChange={e => patchDraft('icon', e.target.value)}
                        style={{ textAlign: 'center', fontSize: 20, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '5px 2px', width: '100%' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Name</div>
                      <input type="text" className="fi" value={editing.draft.name}
                        onChange={e => patchDraft('name', e.target.value)} placeholder="Extra name" autoFocus />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Price (€)</div>
                      <input type="number" className="fi" min={0} value={editing.draft.price}
                        onChange={e => patchDraft('price', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Billing</div>
                      <select className="fsel2" value={editing.draft.perDay ? 'day' : 'flat'}
                        onChange={e => patchDraft('perDay', e.target.value === 'day')}>
                        <option value="day">Per day</option>
                        <option value="flat">Flat fee</option>
                      </select>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 5 }}>Enabled</div>
                      <input type="checkbox" checked={!!editing.draft.enabled}
                        onChange={e => patchDraft('enabled', e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    </div>
                  </div>
                  {/* Quantity settings */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 10px', background: 'var(--sur)', borderRadius: 6, border: '1px solid var(--bd)', marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!editing.draft.allowQty}
                        onChange={e => patchDraft('allowQty', e.target.checked)}
                        style={{ width: 15, height: 15, cursor: 'pointer' }} />
                      <span style={{ fontWeight: 600 }}>Allow multiple quantities</span>
                    </label>
                    <span style={{ fontSize: 10, color: 'var(--tx3)' }}>e.g. 2× child seat, 2× driver</span>
                    {editing.draft.allowQty && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                        <span style={{ fontSize: 10, color: 'var(--tx3)' }}>Max qty:</span>
                        <input type="number" className="fi" min={1} max={10} value={editing.draft.maxQty ?? 3}
                          onChange={e => patchDraft('maxQty', parseInt(e.target.value) || 1)}
                          style={{ width: 60 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveExtra}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
                    <button onClick={() => removeExtra(ex.id)}
                      style={{ background: 'none', border: '1px solid var(--ac)', color: 'var(--ac)', cursor: 'pointer', fontSize: 13, borderRadius: 6, padding: '2px 8px', marginLeft: 'auto' }}>✕ Delete</button>
                  </div>
                </>
              ) : (
                /* Read mode */
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{ex.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{ex.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>€{ex.price} {ex.perDay ? '/ day' : 'flat'}</div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                    background: ex.enabled ? 'var(--gr-l)' : 'var(--sur)',
                    color: ex.enabled ? 'var(--gr)' : 'var(--tx3)',
                    border: `1px solid ${ex.enabled ? 'var(--gr-b)' : 'var(--bd)'}` }}>
                    {ex.enabled ? 'Active' : 'Hidden'}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit('extra', ex)}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>}
      </Card>

      {/* ── Insurance ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('ins')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>🛡️ Insurance Options
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>{insurance.length} plan{insurance.length !== 1 ? 's' : ''}</span>
            </div>
            {!openSections.ins && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          {openSections.ins && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addIns(); }}>+ Add Plan</button>}
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.ins ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.ins && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {insurance.map(ins => (
            <div key={ins.id} style={{
              border: `1.5px solid ${ins.recommended ? 'var(--ac-b)' : 'var(--bd)'}`,
              borderRadius: 'var(--r)', padding: '12px 14px',
              background: ins.recommended ? 'var(--ac-l)' : 'var(--sur2)',
            }}>
              {isEditing('ins', ins.id) ? (
                /* Edit mode */
                <>
                  {/* Icon presets */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Icon presets</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {INS_ICON_PRESETS.map(p => (
                        <button key={p.label} onClick={() => patchDraft('icon', p.icon)}
                          title={p.label}
                          style={{
                            padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 16,
                            border: editing.draft.icon === p.icon ? '1.5px solid var(--ac)' : '1px solid var(--bd)',
                            background: editing.draft.icon === p.icon ? 'var(--ac-l)' : 'var(--sur)',
                            lineHeight: 1.4,
                          }}>
                          {p.icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <input type="text" value={editing.draft.icon} onChange={e => patchDraft('icon', e.target.value)}
                      style={{ width: 60, textAlign: 'center', fontSize: 18, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '4px 6px' }} />
                    <input type="text" value={editing.draft.name} onChange={e => patchDraft('name', e.target.value)}
                      className="fi" style={{ fontWeight: 600, fontSize: 13, flex: 1 }} placeholder="Plan name" autoFocus />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                      <input type="checkbox" checked={!!editing.draft.recommended}
                        onChange={e => patchDraft('recommended', e.target.checked)} />
                      Recommended
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Price per day (€)</div>
                      <input type="number" className="fi" min={0} value={editing.draft.price}
                        onChange={e => patchDraft('price', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Deposit held (€)</div>
                      <input type="number" className="fi" min={0} value={editing.draft.deposit ?? 0}
                        onChange={e => patchDraft('deposit', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Excess</div>
                      <input type="text" className="fi" value={editing.draft.excess}
                        onChange={e => patchDraft('excess', e.target.value)} placeholder="€0" />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Badge colour</div>
                      <select className="fsel2" value={editing.draft.color} onChange={e => patchDraft('color', e.target.value)}>
                        <option value="var(--bd)">Grey</option>
                        <option value="var(--bl)">Blue</option>
                        <option value="var(--gr)">Green</option>
                        <option value="var(--am)">Amber</option>
                        <option value="var(--ac)">Brand</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Description (shown to customer)</div>
                    <input type="text" className="fi" value={editing.draft.desc}
                      placeholder="Short description" onChange={e => patchDraft('desc', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveIns}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
                    <button onClick={() => removeIns(ins.id)}
                      style={{ background: 'none', border: '1px solid var(--ac)', color: 'var(--ac)', cursor: 'pointer', fontSize: 13, borderRadius: 6, padding: '2px 8px', marginLeft: 'auto' }}>✕ Delete</button>
                  </div>
                </>
              ) : (
                /* Read mode */
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 22 }}>{ins.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: ins.color }}>{ins.name}</span>
                      {ins.recommended && <span style={{ fontSize: 9, background: 'var(--ac)', color: '#fff', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>Recommended</span>}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{ins.desc}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx2)', marginTop: 2 }}>
                      {ins.price === 0 ? 'Included' : `€${ins.price}/day`} · Excess: {ins.excess} · Deposit: €{ins.deposit ?? 0}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit('ins', ins)}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>}
      </Card>

      {/* ── Areas / Zones ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('areas')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>🗺️ Areas / Zones
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>{areas.length} area{areas.length !== 1 ? 's' : ''}</span>
            </div>
            {!openSections.areas && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          {openSections.areas && <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addArea(); }}>+ Add Area</button>}
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.areas ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.areas && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {areas.map(area => {
            const locCount = locations.filter(l => l.areaId === area.id).length;
            return (
              <div key={area.id} style={{
                border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px',
                background: 'var(--sur2)',
              }}>
                {isEditing('area', area.id) ? (
                  /* Edit mode */
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 100px', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Icon</div>
                        <input type="text" value={editing.draft.icon} onChange={e => patchDraft('icon', e.target.value)}
                          style={{ textAlign: 'center', fontSize: 20, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '5px 2px', width: '100%' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Name</div>
                        <input type="text" className="fi" value={editing.draft.name}
                          onChange={e => patchDraft('name', e.target.value)} placeholder="Area name" autoFocus />
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 2 }}>Colour</div>
                        <input type="color" value={editing.draft.color || '#1A3A5C'}
                          onChange={e => patchDraft('color', e.target.value)}
                          style={{ width: '100%', height: 32, border: '1px solid var(--bd)', borderRadius: 6, padding: 2, cursor: 'pointer', background: 'var(--sur)' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-primary btn-sm" onClick={saveArea}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
                      <button onClick={() => removeArea(area.id)}
                        style={{ background: 'none', border: '1px solid var(--ac)', color: 'var(--ac)', cursor: 'pointer', fontSize: 13, borderRadius: 6, padding: '2px 8px', marginLeft: 'auto' }}>✕ Delete</button>
                    </div>
                  </>
                ) : (
                  /* Read mode */
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{area.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{area.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: area.color }} />
                        <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{locCount} location{locCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEdit('area', area)}>Edit</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>}
      </Card>

      {/* ── Rental Rules ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('rules')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>📋 Rental Rules
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>Advance booking, buffers & limits</span>
            </div>
            {!openSections.rules && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.rules ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.rules && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div className="fg">
                <label className="fl">Min advance booking (hours)</label>
                <select className="fsel2" value={settings.minAdvanceHours ?? 6}
                  onChange={e => setSettings(s => ({ ...s, minAdvanceHours: parseInt(e.target.value) }))}>
                  {[0, 2, 4, 6, 12, 24, 48].map(h => (
                    <option key={h} value={h}>{h === 0 ? 'No minimum' : `${h} hours`}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                  How far in advance a customer must book before pickup
                </div>
              </div>
              <div className="fg">
                <label className="fl">Buffer between rentals (hours)</label>
                <select className="fsel2" value={settings.bufferHours ?? 12}
                  onChange={e => setSettings(s => ({ ...s, bufferHours: parseInt(e.target.value) }))}>
                  {[0, 3, 6, 12, 24, 48].map(h => (
                    <option key={h} value={h}>{h === 0 ? 'No buffer' : `${h} hours`}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                  Minimum gap between consecutive rentals of the same vehicle
                </div>
              </div>
              <div className="fg">
                <label className="fl">Max advance booking (days)</label>
                <select className="fsel2" value={settings.maxAdvanceDays ?? 90}
                  onChange={e => setSettings(s => ({ ...s, maxAdvanceDays: parseInt(e.target.value) }))}>
                  {[30, 60, 90, 120, 180, 365].map(d => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                  How far into the future customers can book
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => askConfirm('Please confirm that you want to save the rental rules.', () => { setConfirmDlg(null); save(); })}>Save Rules</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSettings(companySettings)}>Reset</button>
            </div>
          </div>
        )}
      </Card>

      {/* ── Customer Requirements ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => toggleSection('reqs')}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>📝 Customer Requirements
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>Fields shown in the booking engine</span>
            </div>
            {!openSections.reqs && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>Click to expand</div>}
          </div>
          <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10, transition: 'transform .2s', display: 'inline-block', transform: openSections.reqs ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
        {openSections.reqs && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'reqPassportNum',   label: 'Passport / ID number' },
              { key: 'reqLicenceNum',    label: 'Driver licence number' },
              { key: 'reqLicExpiry',     label: 'Licence expiry date' },
              { key: 'reqPassportPhoto', label: 'Passport / ID photo upload' },
              { key: 'reqLicencePhoto',  label: 'Licence photo upload' },
              { key: 'reqSignAgreement', label: 'Sign rental agreement' },
            ].map(r => (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)' }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{r.label}</div>
                <select className="fsel2" style={{ width: 130 }}
                  value={settings[r.key] || 'hidden'}
                  onChange={e => setSettings(s => ({ ...s, [r.key]: e.target.value }))}>
                  <option value="hidden">Hidden</option>
                  <option value="optional">Optional</option>
                  <option value="required">Required</option>
                </select>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary btn-sm" onClick={() => askConfirm('Please confirm that you want to save the customer requirements.', () => { setConfirmDlg(null); save(); })}>Save Requirements</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSettings(companySettings)}>Reset</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
